import * as THREE from "three";

export type GeometryAxis = "x" | "y" | "z";

/**
 * Resizes one geometry axis like a three-slice UI panel: both outer bands keep
 * their authored shape and only the middle band changes length. This is used
 * twice for tabletops (once per footprint axis), giving them a nine-slice-like
 * resize without flattening rounded corners and layered rims.
 *
 * `scale` is relative to the geometry's current size. `border_ratio` is the
 * width of EACH protected edge as a fraction of the original full span.
 */
export function centerSliceGeometry(
  geometry: THREE.BufferGeometry,
  axis: GeometryAxis,
  scale: number,
  border_ratio = 0.10
): void
{
  if ( !Number.isFinite( scale ) || scale <= 0 || Math.abs( scale - 1 ) < 1e-6 )
    return;

  const position = geometry.getAttribute( "position" );
  if ( !( position instanceof THREE.BufferAttribute ) )
    return;

  geometry.computeBoundingBox();
  const box = geometry.boundingBox;
  if ( box === null )
    return;

  const source_min = box.min[axis];
  const source_max = box.max[axis];
  const source_span = source_max - source_min;
  if ( source_span <= 1e-8 )
    return;

  const center = ( source_min + source_max ) * 0.5;
  const target_span = source_span * scale;
  const source_border = source_span * THREE.MathUtils.clamp( border_ratio, 0, 0.49 );

  // If a requested table is narrower than both protected borders combined,
  // let the borders meet in the middle instead of crossing over each other.
  const target_border = Math.min( source_border, target_span * 0.5 );
  const source_left_cut = source_min + source_border;
  const source_right_cut = source_max - source_border;
  const target_min = center - target_span * 0.5;
  const target_max = center + target_span * 0.5;
  const target_left_cut = target_min + target_border;
  const target_right_cut = target_max - target_border;

  const component = axis === "x" ? 0 : axis === "y" ? 1 : 2;

  for ( let i = 0; i < position.count; i++ )
  {
    const source = position.getComponent( i, component );
    let target: number;

    if ( source <= source_left_cut )
    {
      const t = ( source - source_min ) / source_border;
      target = THREE.MathUtils.lerp( target_min, target_left_cut, t );
    }
    else if ( source >= source_right_cut )
    {
      const t = ( source - source_right_cut ) / source_border;
      target = THREE.MathUtils.lerp( target_right_cut, target_max, t );
    }
    else
    {
      const source_center_span = source_right_cut - source_left_cut;
      const t = ( source - source_left_cut ) / source_center_span;
      target = THREE.MathUtils.lerp( target_left_cut, target_right_cut, t );
    }

    position.setComponent( i, component, target );
  }

  position.needsUpdate = true;
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
}

/**
 * World-axis version for imported FBX hierarchies. FBX meshes commonly carry
 * baked rotations (the new tables use local X/Y as their footprint), so
 * guessing a local geometry axis can deform thickness instead of width. Each
 * vertex is therefore sliced in world space and transformed back afterwards.
 */
export function centerSliceObjectGeometry(
  object: THREE.Object3D,
  axis: GeometryAxis,
  scale: number,
  border_ratio = 0.10
): void
{
  if ( !Number.isFinite( scale ) || scale <= 0 || Math.abs( scale - 1 ) < 1e-6 )
    return;

  object.updateWorldMatrix( true, true );
  const box = new THREE.Box3().setFromObject( object );
  const source_min = box.min[axis];
  const source_max = box.max[axis];
  const source_span = source_max - source_min;
  if ( source_span <= 1e-8 )
    return;

  const center = ( source_min + source_max ) * 0.5;
  const target_span = source_span * scale;
  const source_border = source_span * THREE.MathUtils.clamp( border_ratio, 0, 0.49 );
  const target_border = Math.min( source_border, target_span * 0.5 );
  const source_left_cut = source_min + source_border;
  const source_right_cut = source_max - source_border;
  const target_min = center - target_span * 0.5;
  const target_max = center + target_span * 0.5;
  const target_left_cut = target_min + target_border;
  const target_right_cut = target_max - target_border;
  const vertex = new THREE.Vector3();

  const remap = ( source: number ): number =>
  {
    if ( source <= source_left_cut )
      return THREE.MathUtils.lerp(
        target_min,
        target_left_cut,
        ( source - source_min ) / source_border
      );

    if ( source >= source_right_cut )
      return THREE.MathUtils.lerp(
        target_right_cut,
        target_max,
        ( source - source_right_cut ) / source_border
      );

    return THREE.MathUtils.lerp(
      target_left_cut,
      target_right_cut,
      ( source - source_left_cut ) / ( source_right_cut - source_left_cut )
    );
  };

  object.traverse( child =>
  {
    if ( !( child instanceof THREE.Mesh ) )
      return;

    const position = child.geometry.getAttribute( "position" );
    if ( !( position instanceof THREE.BufferAttribute ) )
      return;

    const to_world = child.matrixWorld.clone();
    const to_local = to_world.clone().invert();

    for ( let i = 0; i < position.count; i++ )
    {
      vertex.fromBufferAttribute( position, i ).applyMatrix4( to_world );
      vertex[axis] = remap( vertex[axis] );
      vertex.applyMatrix4( to_local );
      position.setXYZ( i, vertex.x, vertex.y, vertex.z );
    }

    position.needsUpdate = true;
    child.geometry.computeBoundingBox();
    child.geometry.computeBoundingSphere();
  } );
}
